import React, {
    useState,
    useContext,
    useCallback,
    useEffect,
    useMemo
} from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import {
    Chevron, Icon, ModalNextProvider
} from '@jutro/components';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import {fnolCommonMessages} from "../../../nexus-capability-fnol-common-react";
import fnolCPMessages from 'gw-capability-fnol-cp-react/FNOLCP.messages';
import { ViewModelForm, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import styles from './DetailsPage.module.scss';
import './DetailsPage.messages';
import metadata from './DetailsPage.metadata.json5';
import FNOLLossLocation from '../../../nexus-capability-fnol-common-react/components/LossLocation/LossLocation';
import { messages as commonMessages } from 'gw-platform-translations';
import { useDependencies } from 'gw-portals-dependency-react';
import { claimsMessages } from '../../../nexus-capability-claim-react';
import messages from '../../../nexus-capability-fnol-common-react/FNOL.messages';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import Address from '../../../nexus-capability-fnol-common-react/models/Address';
import AddressComponent from '../../../gw-capability-common-components/Address/AddressComponent';


function DetailsPage(props) {
    const translator = useContext(TranslatorContext);
    const {
        wizardData: claimVM,
        updateWizardData,
        history,
        authHeader
    } = props;
    const { ClaimDownloadService } = useDependencies('ClaimDownloadService');
    const { ClaimService } = useDependencies('ClaimService');
    const viewModelService = useContext(ViewModelServiceContext);
    const OTHER_ID = 'other';
    const newLocationButtonData = { displayName: 'Other Location', id: OTHER_ID };
    const [selectedLossLocation, setSelectedLossLocation] = useState();
    const [showLoader, setShowLoader] = useState(false);
    const {
        onValidate, isComponentValid, initialValidation, registerInitialComponentValidation
    } = useValidation('DetailsPage');

    const initialiseVM = useCallback(() => {
        return claimVM;
    }, [claimVM]);

    const updatePropertyIncidents = useCallback(
        (incidents, locations) => {
            let newIncidentList = [];
            if (locations) {
                incidents.forEach((incident) => {
                    if (locations.some((location) => location.number === incident.location)) {
                        newIncidentList = [...newIncidentList, incident];
                    }
                });
                locations.forEach((location) => {
                    if (!incidents.some((incident) => {
                        return location.number === incident.location;
                    })) {
                        newIncidentList = [...newIncidentList, { location: location.number }];
                    }
                });
            }
            _.set(claimVM, 'lobs.farm.fixedPropertyIncidents.value', newIncidentList);
            updateWizardData(claimVM);
        }, [claimVM, updateWizardData]
    );

    useEffect(
        () => {
            const nextclaimVM = initialiseVM();
            let locationId = _.get(claimVM.value, 'lossLocation.publicID');
            if (_.isEmpty(locationId) && !_.isEmpty(_.get(claimVM.value, 'lossLocation'))) {
                locationId = OTHER_ID;
            }
            setSelectedLossLocation(locationId);
            const propertyIncidentsVM = _.get(claimVM, 'lobs.farm.fixedPropertyIncidents.value');
            const faPolicyExtension = _.get(claimVM, 'policy.lobs.farm.locations.value');
            updatePropertyIncidents(propertyIncidentsVM, faPolicyExtension);
            updateWizardData(nextclaimVM);
            // execure once
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []
    );

    const renderLocationAccordionHeader = useCallback(
        (location) => {
            const buildingData = _.get(claimVM.value, 'policy.lobs.farm.propertyRiskUnits');
            const buildingFilteredData = buildingData.filter((data) => data.locationNumber
                === location.number);
            const noOfBuilding = buildingFilteredData ? buildingFilteredData.length : 0;
            return (isOpen) => (
            <React.Fragment>
                <Chevron isOpen={isOpen} align="right" />
                <div className={styles.faLocationHeader}>
                    <span className={styles.faDetailAddressClass}>
                        <Icon icon="mi_place" />
                        {location.address.displayName}
                    </span>
                    <span className={styles.countDetails}>
                        {noOfBuilding}
                        <Icon icon="mi-business" />
                    </span>
                </div>
            </React.Fragment>
            );
        }, [claimVM]
    );

    const getEstimateReceivedStatus = useCallback(
        (index) => {
            const incidentPath = `lobs.farm.fixedPropertyIncidents.children[${index}].value`;
            const selectedIncident = _.get(claimVM, incidentPath);
            return !_.isEmpty(selectedIncident) && (selectedIncident.estimateReceived === 'Yes');
        }, [claimVM]
    );

    const generateLocationOverrides = useCallback(
        () => {
            const locationsPath = 'lobs.farm.fixedPropertyIncidents';
            const incident = _.get(claimVM, `${locationsPath}.value`);
            const locations = _.filter(_.get(claimVM, 'policy.lobs.farm.locations.value'),
                (location) => {
                    return incident.some((incidentobj) => {
                        return location.number === incidentobj.location;
                    });
                });

            const overrides = locations.map((location, index) => {
                const overridePath = `${locationsPath}.children[${index}]`;
                return {
                    [`faClaimSectionId${index}`]: {
                        header: renderLocationAccordionHeader(location, `${overridePath}.value`)
                    },
                    [`faClaimEstimateRepairCost${index}`]: {
                        visible: getEstimateReceivedStatus(index)
                    },
                    [`faClaimEstimatedRepairTime${index}`]: {
                        visible: getEstimateReceivedStatus(index)
                    }
                };
            });

            return Object.assign({}, ...overrides);
        },
        [claimVM, getEstimateReceivedStatus, renderLocationAccordionHeader]
    );

    const getFileExtension = useCallback((name) => {
        const extension = name.split('.').pop();
        return extension || '';
    }, []);

    const getFileIcon = useCallback((extension) => {
        switch (extension) {
            case 'doc':
            case 'docx':
            case 'txt':
            case 'docm':
                return 'file-word';
            case 'xlt':
            case 'xlsx':
            case 'xlsm':
            case 'xls':
                return 'file-excel';
            case 'gif':
            case 'png':
            case 'jpeg':
                return 'file-image';
            case 'pdf':
                return 'file-pdf';
            default:
                return 'file';
        }
    }, []);

    const changeUploadDocmentImageType = useCallback(
        (documentUploadItem) => {
            const extension = getFileExtension(documentUploadItem.docUID).toLowerCase() || '';
            return `${getFileIcon(extension)}`;
        },
        [getFileExtension, getFileIcon]
    );

    const showDocumentDeleteIcon = useCallback((documentUploadItem) => {
        if (documentUploadItem.canDelete) {
            return true;
        }
        return false;
    }, []);

    const handleError = useCallback(
        (title, message) => {
            return ModalNextProvider.showAlert({
                title: title,
                message: message,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).catch(_.noop);
        }, []
    );

    const onDeleteDocumentIcon = useCallback(
        (e, item) => {
            e.preventDefault();
            const { claimNumber, publicID, name } = item;
            ModalNextProvider.showConfirm({
                title: claimsMessages.confirmationToRemoveTitle,
                message: translator(messages.fnolRemoveUploadedDocument, { documentName: name }),
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: claimsMessages.removeConfirmationYes,
                cancelButtonText: claimsMessages.removeConfirmationNo
            }).then(async (results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                setShowLoader(true);
                try {
                    const isDeleteItem = await ClaimService.claimsRemoveDocument(
                        [claimNumber, publicID],
                        authHeader
                    );
                    if (isDeleteItem === false) {
                        setShowLoader(false);
                        return handleError(
                            claimsMessages.deletionFailedTitle,
                            claimsMessages.deletionFailedMessage
                        );
                    }
                    const documentsArray = _.get(claimVM, 'documents.value');
                    const updatedDocArray = documentsArray.filter((documentObj) => {
                        return documentObj.publicID !== publicID;
                    });
                    const newClaimVM = _.clone(claimVM);
                    _.set(newClaimVM, 'documents.value', updatedDocArray);
                    updateWizardData(newClaimVM);
                    setShowLoader(false);
                    return true;
                } catch (documentDeletionError) {
                    setShowLoader(false);
                    return handleError(
                        claimsMessages.removeServiceFailedTitle,
                        claimsMessages.removeServiceFailedMessage
                    );
                }
            }, _.noop);
        },
        [translator, ClaimService, authHeader, claimVM, updateWizardData, handleError]
    );


    const handleDocumentDownload = useCallback(
        (item) => {
            if (_.isNil(item)) {
                return null;
            }
            const { workingPublicID, sessionID } = item;
            return ClaimDownloadService
                .getClaimDocument(workingPublicID, sessionID);
        },
        [ClaimDownloadService]
    );

    const claimsDocUploadToken = useCallback(async () => {
        try {
            const uploadTokenID = await ClaimService.claimsDocUploadToken([], authHeader);
            return uploadTokenID;
        } catch (e) {
            return handleError(
                commonMessages.errorUploadTitle,
                commonMessages.errorGenerateUploadToken
            );
        }
    }, [ClaimService, authHeader, handleError]);

    const onUploadDocument = useCallback(
        async (file) => {
            setShowLoader(true);
            const documentMetaDataTemplate = {
                DocUID: '001',
                DocumentType: 'fnol',
                SecurityType: 'unrestricted',
                Status: 'approved',
                Author: 'Policy Holder',
                claimNumber: claimVM.claimNumber.value,
                name: file.name,
                mimeType: file.type,
                sessionID: await claimsDocUploadToken()
            };
            try {
                const data = await ClaimService.uploadDocument(
                    file,
                    documentMetaDataTemplate,
                    authHeader
                );
                const documentsArray = _.get(claimVM, 'documents.value');
                documentsArray.push(data);
                const newClaimVM = _.clone(claimVM);
                _.set(newClaimVM, 'documents.value', documentsArray);
                updateWizardData(newClaimVM);
                setShowLoader(false);
            } catch (error) {
                setShowLoader(false);
                handleError(commonMessages.errorUploadTitle, commonMessages.uploadFailedMessage);
            }
        },
        [claimVM, claimsDocUploadToken, ClaimService, authHeader, updateWizardData, handleError]
    );

    const getPolicyLocations = useMemo(() => {
        const lossLocations = _.get(claimVM.value, 'predefinedLossLocations');
        let formattedLossLocations;
        if (!_.isEmpty(lossLocations)) {
            formattedLossLocations = lossLocations.map((location) => {
                return {
                    displayName: `${location.displayName}`,
                    id: location.publicID,
                    iconName: '',
                    secondaryLabel: ''
                };
            });
        }
        return [...formattedLossLocations, newLocationButtonData];

    }, [claimVM.value]);

    const onLossLocationAddressSelect = useCallback(
        (selectedValue) => {
            if (selectedValue === 'other') {
                const addressViewModel = viewModelService.create(
                    new Address(),
                    'cc',
                    'edge.capabilities.address.dto.AddressDTO'
                );
                _.set(claimVM, 'lossLocation', addressViewModel.value);
            } else {
                const selectedLocations = _.filter(_.get(claimVM.value, 'predefinedLossLocations'), (location) => {
                    return location.publicID === selectedValue;
                });
                _.set(claimVM, 'lossLocation', selectedLocations[0]);
            }
            setSelectedLossLocation(selectedValue);
            updateWizardData(claimVM);
        },
        [claimVM, updateWizardData]
    );

    const onAddressChange = (value, path) => {
        _.set(claimVM.lossLocation, path, value);
        updateWizardData(claimVM);
    };

    const generateMoreInfoDocumentsOverrides = useCallback(() => {
        const documentUploadPath = 'value.documents';
        const documentUploadItems = _.get(claimVM, documentUploadPath, []);
        const overrides = documentUploadItems.map((documentUploadItem, index) => {
            return {
                [`documentLeftSection${index}`]: {
                    icon: changeUploadDocmentImageType(documentUploadItem)
                },
                [`documentNameLink${index}`]: {
                    href: handleDocumentDownload(documentUploadItem)
                },
                [`documentDeleteSection${index}`]: {
                    onClick: (e) => {
                        onDeleteDocumentIcon(e, documentUploadItem);
                    }
                },
                [`documentDeleteIcon${index}`]: {
                    visible: showDocumentDeleteIcon(documentUploadItem),
                    onClick: (e) => {
                        onDeleteDocumentIcon(e, documentUploadItem);
                    }
                }
            };
        });
        return Object.assign({}, ...overrides);
    }, [
        changeUploadDocmentImageType,
        claimVM,
        handleDocumentDownload,
        onDeleteDocumentIcon,
        showDocumentDeleteIcon
    ]);

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showOptional: false,
            showRequired: true
        },
        faLossLocationComponent: {
            path: !_.isUndefined(claimVM) ? claimVM : null,
            updatePath: updateWizardData
        },
        faAddtionalInformationLoader: {
            loaded: !showLoader
        },
        uploadDocumentGrid: {
            visible: !showLoader
        },
        addressCards: {
            availableValues: getPolicyLocations,
            value:
                selectedLossLocation
                || (!_.isEmpty(claimVM.value) && _.get(claimVM.value, 'lossLocation.publicID')),
            onValueChange: onLossLocationAddressSelect
        },
        otherAddress: {
            visible: selectedLossLocation === OTHER_ID,
            model: claimVM.lossLocation,
            hideFields: ['addressType'],
            onValueChange: onAddressChange
        },
        ...generateLocationOverrides(),
        ...generateMoreInfoDocumentsOverrides(),
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            losslocationcomponent: FNOLLossLocation,
            addressComponent: AddressComponent
        },
        resolveCallbackMap: {
            onValidate: onValidate,
            onUploadDocument: onUploadDocument,
        }
    };

    const { claimStatus } = history.location.state;

    const validateClaimStatus = useCallback(() => {
        return !_.isEmpty(claimStatus) && _.get(claimVM.value, 'lossLocation')
            && claimStatus === translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'Draft' });
    }, [claimVM, claimStatus, translator]);

    useEffect(() => {
        registerInitialComponentValidation(validateClaimStatus);
    }, [validateClaimStatus, registerInitialComponentValidation]);

    return (
        <WizardPage
            nextLabel={translator(fnolCommonMessages.reviewInformation)}
            cancelLabel={translator(fnolCommonMessages.fnolSaveandExit)}
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
                onValidationChange={onValidate}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

DetailsPage.propTypes = wizardProps;
export default withRouter(withAuthenticationContext(DetailsPage));
